import React, { useState, useEffect } from 'react'
import { CandidateReviewResultDto } from '../../../api/role/models/getCandidateReviewResults/GetCandidateReviewResultsRes'
import { CandidateEvaluationDto } from '../../../api/role/models/getCandidateEvaluations/GetCandidateEvaluationsRes'
import { PersonDto } from '../../../api/person/models/PersonDto'
import { CompanyDto } from '../../../api/company/models/CompanyDto'
import { MapExtra } from '../../../utils/MapExtra'
import { TransformedResolvedPerson } from '../../../helixSearch/HelixSearchTypes'
import {
    getCountryFlag,
    getProfilePicUrl,
    formatPersonName,
} from '../../../utils/personUtils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faAddressCard,
    faCheckCircle,
    faXmarkCircle,
} from '@fortawesome/free-solid-svg-icons'
import Card from '../../common/cards/Card'
import Button from '../../common/form/inputs/Button'
import NotAFitModal from './NotAFitModal'
import { useSafeUserData } from '../../../stateStores/User'
import Note from '../../common/note/Note'
import { NoteType } from '../../../enums/NoteType'
import PersonProfileModal from '../../person/profile/PersonProfileModal'
import CandidateConnectionsSingleSeed from './CandidateConnectionsSingleSeed'
import { NotAFitPreference } from '../../../api/role/models/candidateReviewAction/CandidateReviewActionReq'

interface Props {
    result?: CandidateReviewResultDto
    person?: PersonDto
    evaluation?: CandidateEvaluationDto
    connections?: TransformedResolvedPerson
    companiesById: MapExtra<string, CompanyDto>
    isSelected: boolean
    status: string
    onCardClick: () => void
    onActionAndAdvance: (
        personId: string,
        status: string,
        evalComment?: string,
        notAFitPreference?: NotAFitPreference,
    ) => void
    scrollPendingPersonId: string | null
    setScrollPendingPersonId: (personId: string | null) => void
    index: number
}

export default function CandidateReviewCard({
    person,
    evaluation,
    connections,
    companiesById,
    isSelected,
    status,
    onCardClick,
    onActionAndAdvance,
    scrollPendingPersonId,
    setScrollPendingPersonId,
    index,
}: Props): React.JSX.Element {
    const [showComment, setShowComment] = useState(false)
    const [comment, setComment] = useState(evaluation?.evalComment || '')
    const [showNotAFitModal, setShowNotAFitModal] = useState(false)
    const { user } = useSafeUserData()
    // New: Full profile modal state
    const [isProfileOpen, setIsProfileOpen] = useState(false)

    // Keep comment state in sync when evaluation changes and auto-open if a comment exists
    useEffect(() => {
        const existing = evaluation?.evalComment || ''
        setComment(existing)
        if (existing.trim().length > 0) {
            setShowComment(true)
        }
    }, [evaluation?.evalComment])

    const handleNotAFitClick = () => {
        console.log('Not a fit clicked for candidate:', person?.id)

        // Check if this user already has a manual relationship score with this candidate
        if (connections?.companies) {
            for (const company of connections.companies) {
                for (const connection of company.connections) {
                    console.log('Checking connection:', {
                        connectionSeedPersonId: connection.seedPersonId,
                        currentUserPersonId: user.personId,
                        relationshipInfo: connection.relationshipInfo,
                        hasManualScore:
                            connection.relationshipInfo
                                ?.workXoverManualScore !== undefined,
                    })

                    // Check if this connection is from the current user and has a manual score
                    if (
                        connection.seedPersonId === user.personId &&
                        connection.relationshipInfo?.workXoverManualScore !==
                            undefined
                    ) {
                        console.log(
                            'Manual score already exists, not showing modal. Saving Not a Fit immediately.',
                        )
                        // Save the evaluation and comment without showing the modal
                        if (person?.id) {
                            onActionAndAdvance(
                                person.id,
                                'Not a Fit',
                                comment,
                                // Do not pass preference; BE ignores it when manual exists
                                undefined,
                            )
                        }
                        return
                    }
                }
            }
        }

        setShowNotAFitModal(true)
    }

    const handleModalShowInFuture = async () => {
        if (!person?.id) return

        // Close modal and persist via parent handler with preference
        setShowNotAFitModal(false)
        onActionAndAdvance(
            person.id,
            'Not a Fit',
            comment,
            NotAFitPreference.SHOW_AGAIN,
        )
    }

    const handleModalDontShowAgain = async () => {
        if (!person?.id) return

        // Close modal and persist via parent handler with preference
        setShowNotAFitModal(false)
        onActionAndAdvance(
            person.id,
            'Not a Fit',
            comment,
            NotAFitPreference.DONT_SHOW_AGAIN,
        )
    }

    const handleCloseModal = () => {
        setShowNotAFitModal(false)
    }

    // Handle scrolling when card becomes selected, independent of animation
    useEffect(() => {
        if (isSelected && scrollPendingPersonId === person?.id && index > 0) {
            // Immediate scroll without delay to test if animation is the issue
            const element = document.getElementById(`card-${person.id}`)
            if (element) {
                console.log(
                    'Scrolling to card:',
                    person?.firstName,
                    person?.lastName,
                )
                const elementRect = element.getBoundingClientRect()
                const viewportHeight = window.innerHeight
                const currentScrollY = window.pageYOffset

                console.log('Card dimensions:', {
                    top: elementRect.top,
                    bottom: elementRect.bottom,
                    height: elementRect.height,
                    viewportHeight,
                })

                // Always scroll to position the card at 100px from top
                const elementTopRelativeToDocument =
                    elementRect.top + currentScrollY
                const targetScrollY = elementTopRelativeToDocument - 100

                console.log('Scroll calculation:', {
                    elementTopRelativeToDocument,
                    targetScrollY,
                    currentScrollY,
                })

                window.scrollTo({
                    top: Math.max(0, targetScrollY),
                    behavior: 'smooth',
                })
            }
            setScrollPendingPersonId(null)
        }
    }, [
        isSelected,
        scrollPendingPersonId,
        person?.id,
        person?.firstName,
        person?.lastName,
        index,
        setScrollPendingPersonId,
    ])

    // Status configuration
    const STATUS_CONFIG: Record<
        string,
        { label: string; dotClass: string; ringClass: string }
    > = {
        Recommend: {
            label: 'Recommended',
            dotClass: 'bg-white',
            ringClass: 'ring-1 ring-green-500',
        },
        'Not a Fit': {
            label: 'Not a Fit',
            dotClass: 'bg-white',
            ringClass: 'ring-1 ring-red-400',
        },
        "Don't Know Them": {
            label: 'Unrecognised',
            dotClass: 'bg-white',
            ringClass: 'ring-1 ring-gray-400',
        },
        'Needs Review': {
            label: 'Needs Review',
            dotClass: 'bg-ctxt-blue',
            ringClass: 'ring-1 ring-gray-300',
        },
    }

    // Connections rendering handled by CandidateConnectionsSingleSeed

    if (!person) {
        return (
            <Card className="text-center text-gray-500">
                <div>Person data not available</div>
            </Card>
        )
    }

    const statusConfig = STATUS_CONFIG[status]
    let cardRingClass = ''

    if (isSelected) {
        // Thicken ring to ring-2
        cardRingClass = statusConfig.ringClass.replace('ring-1', 'ring-2')

        // Override colour to ctxt-blue only if still Needs Review
        if (status === 'Needs Review') {
            cardRingClass = 'ring-2 ring-ctxt-blue'
        }
    } else {
        cardRingClass = statusConfig.ringClass
    }

    return (
        <div className="flex flex-col mb-2 w-full">
            <div
                className={`w-full rounded-lg p-4 relative ${cardRingClass} ${
                    isSelected ? '' : 'cursor-pointer'
                }`}
                onClick={() => !isSelected && onCardClick()}
            >
                {/* Status dot */}
                <div
                    className={`absolute top-2 left-2 w-3 h-3 rounded-full ${statusConfig.dotClass}`}
                />

                <div
                    className={`flex items-center gap-4 ${
                        isSelected ? '' : 'h-[70px]'
                    }`}
                >
                    {/* Avatar + reserved space */}
                    <div className="flex items-center justify-start w-[60px] flex-shrink-0 relative">
                        <img
                            src={getProfilePicUrl(person, true)}
                            alt={formatPersonName(person)}
                            className="w-[60px] h-[60px] object-cover rounded-full shrink-0"
                            onError={(e) => {
                                e.currentTarget.onerror = null
                                e.currentTarget.src = '/no-profile-pic.svg'
                            }}
                        />
                    </div>

                    {/* Text content */}
                    <div className="flex flex-col self-center flex-1">
                        <div className="line-clamp-1 font-bold">
                            {formatPersonName(person)}
                        </div>

                        <div className="text-sm text-gray-500 mt-2">
                            <div className="flex mt-[-0.5rem] flex-col">
                                <div className="line-clamp-2">
                                    {person?.headline}
                                </div>
                                <div className="text-gray-600 text-sm line-clamp-1">
                                    {getCountryFlag(
                                        person?.location
                                            ?.split(',')
                                            .pop()
                                            ?.trim() ?? '',
                                    )}
                                    <span className="ml-2">
                                        {person?.location}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Expanded section - ANIMATION TEMPORARILY REMOVED FOR TESTING */}
                {isSelected && (
                    <div className="mt-2">
                        <div className="w-full">
                            {/* Work history section */}
                            {connections && (
                                <CandidateConnectionsSingleSeed
                                    connections={connections}
                                    companiesById={companiesById}
                                />
                            )}

                            {/* Action links */}
                            <div className="mt-2">
                                <button
                                    className="text-sm text-blue-500 hover:underline flex items-center gap-2"
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setIsProfileOpen(true)
                                    }}
                                >
                                    <FontAwesomeIcon icon={faAddressCard} />
                                    <span>View full profile</span>
                                </button>
                            </div>

                            <div className="mt-2">
                                {!showComment ? (
                                    <button
                                        onClick={() => setShowComment(true)}
                                        className="text-sm text-blue-500 hover:underline block"
                                    >
                                        Add comment about {person?.firstName}
                                    </button>
                                ) : (
                                    <div className="mb-2">
                                        <textarea
                                            value={comment}
                                            onChange={(e) =>
                                                setComment(e.target.value)
                                            }
                                            placeholder={`Add your comment about ${person?.firstName}...`}
                                            className="w-full p-2 border border-gray-300 rounded text-sm resize-none"
                                            rows={3}
                                        />
                                        <Note type={NoteType.WARNING}>
                                            Comments are visible to others
                                            reviewing this role
                                        </Note>
                                    </div>
                                )}
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row gap-2 w-full mt-2">
                                <Button
                                    type="button"
                                    style="primary"
                                    classes="w-full sm:flex-1 bg-green-500 hover:bg-green-600 border-0"
                                    onClick={() => {
                                        onActionAndAdvance(
                                            person!.id,
                                            'Recommend',
                                            comment,
                                        )
                                    }}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        <span>Recommend</span>
                                    </span>
                                </Button>

                                <Button
                                    type="button"
                                    style="danger"
                                    classes="w-full sm:flex-1 bg-red-400 hover:bg-red-500"
                                    onClick={handleNotAFitClick}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <FontAwesomeIcon icon={faXmarkCircle} />
                                        <span>Not a Fit</span>
                                    </span>
                                </Button>

                                <Button
                                    type="button"
                                    style="secondary"
                                    classes="w-full sm:flex-1"
                                    onClick={() => {
                                        onActionAndAdvance(
                                            person!.id,
                                            "Don't Know Them",
                                            comment,
                                        )
                                    }}
                                >
                                    <span className="flex items-center justify-center gap-2">
                                        <FontAwesomeIcon icon={faXmarkCircle} />
                                        <span>Don't Know Them</span>
                                    </span>
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Not a Fit Modal */}
            {showNotAFitModal && person && (
                <NotAFitModal
                    person={person}
                    onClose={handleCloseModal}
                    onShowInFuture={handleModalShowInFuture}
                    onDontShowAgain={handleModalDontShowAgain}
                />
            )}

            {/* Full Profile Modal */}
            {isProfileOpen && person && (
                <PersonProfileModal
                    isOpen={isProfileOpen}
                    onClose={() => setIsProfileOpen(false)}
                    person={person}
                    connectionsContent={
                        connections ? (
                            <CandidateConnectionsSingleSeed
                                connections={connections}
                                companiesById={companiesById}
                            />
                        ) : undefined
                    }
                />
            )}
        </div>
    )
}
